//@ts-ignore
import io, { Socket } from 'socket.io-client';
import { eventChannel } from 'redux-saga';
import { fork, take, call, put, cancel, takeLatest } from 'redux-saga/effects';
import {
    sendMessage,
    sendMessageSuccess,
    retrieveUserRooms,
    userTyping,
    joinChatRoom,
    typingStatusSocket,
    userHasReadMessage,
    joinAllChatRooms,
    newContent,
    setNewContentAvailable,
    getMessagesForSelectedRoom,
    getMyRoomsSuccess,
    leaveRoomAction,
    updateUnreadMessageCount,
    sendMessageFail,
    updateMessageSuccess,
    deleteMessageSuccess,
    reactToMessageSuccess,
    updateMessage,
    deleteMessage,
    reactToMessage,
} from './actions';

import { SOCKET } from '../../configurations/api/url';
import { IMessagePayload, SOCKET_REQUESTS } from './types';
import { retrieveJwt } from '../authentication';
import { types, NewContentType } from './types';
import { setRoomMessages } from './actions';
import { getJWtDetails } from '../../utils/AppUtils';
import { disconnect, setOnlineUsers, setOfflineUsers, setOnlineStatus } from '../live/action';
import { setSocketError } from '../error/actions';
import { logoutSuccess } from '../authentication/action';

function connect(jwt: any, userID: string) {
    const socket = io(SOCKET, {
        forceNew: true,
        transportOptions: {
            polling: {
                extraHeaders: {
                    Authorization: `Bearer ${jwt}`,
                    'X-User': userID,
                },
            },
        },
    });
    socket.emit(SOCKET_REQUESTS.ONLINE, { status: 'online' });
    return new Promise(resolve => {
        socket.on('connect', () => {
            resolve(socket);
        });
    });
}

function subscribe(socket: Socket) {
    return eventChannel(emit => {
        /** The below shoudl get all messages and we then sort them by RoomID */
        socket.on(SOCKET_REQUESTS.GET_ROOMS, ({ rooms, pagination }) => {
            /** Dispatch the success here */
            emit(getMyRoomsSuccess({ rooms, pagination }));
        });

        socket.on(SOCKET_REQUESTS.MESSAGE_EDITED, ({ updatedMessage, currentRoom }) => {
            /** Dispatch the success here */
            emit(updateMessageSuccess({ updatedMessage, currentRoom }));
        });

        socket.on(SOCKET_REQUESTS.MESSAGE_DELETED, ({ messageId, currentRoom }) => {
            /** Dispatch the success here */
            emit(deleteMessageSuccess({ messageId, currentRoom }));
        });

        socket.on(SOCKET_REQUESTS.REACTION_UPDATED, ({ updatedMessage, room, action }) => {
            /** Dispatch the success here */
            emit(reactToMessageSuccess({ updatedMessage, room, action }));
        });

        socket.on(SOCKET_REQUESTS.NEW_MESSAGE, ({ newMessage, currentRoom, uniqueSocketID }) => {
            const payload = newMessage;
            payload.newRoom = currentRoom;
            payload.uniqueSocketID = uniqueSocketID;
            emit(sendMessageSuccess(payload));
        });

        socket.on(
            SOCKET_REQUESTS.FAILED_MESSAGE,
            ({ payload, uniqueSocketID }: { payload: IMessagePayload; uniqueSocketID: string }) => {
                emit(
                    sendMessageFail({
                        payload,
                        uniqueSocketID,
                    }),
                );
            },
        );
        socket.on(SOCKET_REQUESTS.DISCONNECT, e => {
            // TODO: handle
        });

        socket.on(SOCKET_REQUESTS.USER_TYPING, ({ status, roomID, userId }) => {
            // emit an action
            emit(userTyping({ status, roomID, userId }));
        });

        socket.on(SOCKET_REQUESTS.ROOM_MESSAGES, ({ messages, pagination }) => {
            emit(setRoomMessages({ messages, pagination }));
        });

        socket.on(SOCKET_REQUESTS.SET_ONLINE_USERS, ({ user }) => {
            emit(setOnlineUsers(user));
        });

        socket.on(SOCKET_REQUESTS.SET_OFFLINE_USERS, ({ user }) => {
            emit(setOfflineUsers(user));
        });

        socket.on(SOCKET_REQUESTS.NEW_CONTENT_AVAILABLE, ({ content_type }: { content_type: NewContentType }) => {
            emit(setNewContentAvailable(content_type));
        });

        socket.on(
            SOCKET_REQUESTS.UNREAD_COUNT_UPDATE,
            ({ unreadCount, roomID, readBy }: { unreadCount: number; roomID: string; readBy: string }) => {
                emit(updateUnreadMessageCount({ unreadCount, roomID, readBy }));
            },
        );

        socket.on(SOCKET_REQUESTS.ERROR, ({ message, status }: { message: string; status: number }) => {
            emit(
                setSocketError({
                    data: {
                        message: message,
                        status: 'failed',
                    },
                    status: status,
                    statusText: message,
                }),
            );
        });

        return () => {};
    });
}

function* read(socket: Socket): any {
    const channel = yield call(subscribe, socket);
    while (true) {
        const action = yield take(channel);
        yield put(action);
    }
}

function* write(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${sendMessage}`);
        socket.emit('message', payload);
        socket.emit('typingStatus', { status: 'no', roomID: payload.roomID });
    }
}

/** This Actually gets Rooms and not messages */
function* getUserRooms(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${retrieveUserRooms}`);
        /** Dispatch the initial action here */
        socket.emit(SOCKET_REQUESTS.RETRIEVE_ROOMS, payload);
    }
}

function* userHasRead(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${userHasReadMessage}`);
        socket.emit(SOCKET_REQUESTS.USER_HAS_READ, payload);
    }
}

function* joinRoom(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${joinChatRoom}`);
        socket.emit(SOCKET_REQUESTS.JOIN_ROOM, payload);
    }
}

function* leaveRoom(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${leaveRoomAction}`);
        socket.emit(SOCKET_REQUESTS.LEAVE_ROOM, payload);
    }
}

function* joinAllRooms(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${joinAllChatRooms}`);
        socket.emit(SOCKET_REQUESTS.JOIN_ALL_ROOMS, payload);
    }
}

/** Get the messages for a particular room */
function* getRoomMessages(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${getMessagesForSelectedRoom}`);
        socket.emit(SOCKET_REQUESTS.GET_ROOM_MESSAGES, payload);
    }
}

/** Typing status for user */
function* typingStatus(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${typingStatusSocket}`);
        socket.emit(SOCKET_REQUESTS.USER_TYPING, payload);
    }
}

/** Set whether a user is online or offline */
function* setUserOnlineStatus(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${setOnlineStatus}`);
        socket.emit(SOCKET_REQUESTS.ONLINE, { status: payload });
    }
}

function* newContentDispatcher(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${newContent}`);
        socket.emit(SOCKET_REQUESTS.NEW_CONTENT, { content_type: payload });
    }
}

function* updateMessageSocket(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${updateMessage}`);
        socket.emit(SOCKET_REQUESTS.EDIT_MESSAGE, payload);
    }
}

function* deleteMessageSocket(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${deleteMessage}`);
        socket.emit(SOCKET_REQUESTS.DELETE_MESSAGE, payload);
    }
}

function* reactToMessageSocket(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${reactToMessage}`);
        socket.emit(SOCKET_REQUESTS.EDIT_REACTION, payload);
    }
}

function* logoutTrigger(socket: Socket) {
    while (true) {
        yield take(`${logoutSuccess}`);
        socket.emit(SOCKET_REQUESTS.LOGOUT);
    }
}

function* handleIO(socket: Socket) {
    yield fork(newContentDispatcher, socket);
    yield fork(setUserOnlineStatus, socket);
    yield fork(read, socket);
    yield fork(write, socket);
    yield fork(getUserRooms, socket);
    yield fork(joinRoom, socket);
    yield fork(leaveRoom, socket);
    yield fork(joinAllRooms, socket);
    yield fork(typingStatus, socket);
    yield fork(userHasRead, socket);
    yield fork(getRoomMessages, socket);
    yield fork(updateMessageSocket, socket);
    yield fork(deleteMessageSocket, socket);
    yield fork(reactToMessageSocket, socket);
    yield fork(logoutTrigger, socket);
}

function* flow(): any {
    while (true) {
        const jwt = yield retrieveJwt();
        const { isSignedIn, user_id } = getJWtDetails(jwt);
        if (isSignedIn) {
            const socket = yield call(connect, jwt, user_id);
            const task = yield fork(handleIO, socket);
            yield take(`${disconnect}`);
            yield cancel(task);
            socket.emit(SOCKET_REQUESTS.DISCONNECT);
        }
    }
}

function* flowWatcher() {
    yield takeLatest(types.CONNECT, flow);
}

export default function* socketSaga() {
    yield fork(flowWatcher);
}
